import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown } from "lucide-react";
import { COUNTRIES, codeToFlag, parseCountryFromNumber } from "@/lib/countries";

/**
 * PhoneInput — country-picker dropdown (flag + name + dial code) + national number input.
 * Emits a single string in E.164 form: "+62 812345678" → onChange("+62812345678").
 *
 * Props:
 *  - value              string   current E.164 value (may be empty)
 *  - onChange           fn(v)    called with the merged "+<dial><local>" string
 *  - defaultCountryCode string   ISO-2 default when value is empty (default: "ID")
 *  - required, disabled, placeholder, id, name, className   forwarded to the number input
 *  - testid             string   base data-testid for the input
 */
export default function PhoneInput({
  value = "",
  onChange,
  defaultCountryCode = "ID",
  placeholder = "812 3456 7890",
  className = "",
  testid = "phone",
  ...inputProps
}) {
  const [open, setOpen] = useState(false);
  // Iter-294 · CRITICAL FIX: sticky ISO for the user's explicit pick.
  // Without this, selecting Bahrain (+973) with an empty local number
  // emitted onChange("") → the country display reverted to defaultCode
  // ("+62" / Indonesia) the moment the parent re-rendered with a fresh
  // key or reset value. The sticky state survives value="" transitions.
  const [pickedCode, setPickedCode] = useState(null);

  const derived = React.useMemo(() => {
    const c = parseCountryFromNumber(value, defaultCountryCode);
    return c;
  }, [value, defaultCountryCode]);

  // Effective country: honour the operator's pick when the value is empty
  // (or when the value's dial matches the pick — e.g. after typing digits
  // into a Bahrain-anchored input). Otherwise fall back to the value-parsed
  // country so external DB-loaded numbers still render their true flag.
  const country = React.useMemo(() => {
    if (pickedCode) {
      const picked = COUNTRIES.find((c) => c.code === pickedCode);
      if (picked) {
        if (!value) return picked;
        // If the value's dial already matches the pick, stick with it.
        if (value.startsWith(`+${picked.dial}`)) return picked;
      }
    }
    return derived;
  }, [pickedCode, derived, value]);

  const local = React.useMemo(() => {
    if (!value) return "";
    if (value.startsWith(`+${country.dial}`)) return value.slice(country.dial.length + 1);
    return value.replace(/^\+/, "");
  }, [value, country.dial]);

  const emit = (c, l) => {
    const digits = (l || "").replace(/\D/g, "");
    onChange?.(digits ? `+${c.dial}${digits}` : "");
  };

  const pickCountry = (c) => {
    setPickedCode(c.code);
    setOpen(false);
    emit(c, local);
  };

  const handleLocalChange = (e) => {
    const cleaned = e.target.value.replace(/[^\d\s-]/g, "");
    emit(country, cleaned);
  };

  return (
    <div className={`flex w-full ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 h-9 rounded-l-md border border-r-0 border-input bg-transparent text-sm hover:bg-muted/40 transition-colors shrink-0"
            data-testid={`${testid}-country-trigger`}
            aria-label={`Country code: ${country.name} +${country.dial}`}
          >
            <span className="text-base leading-none">{codeToFlag(country.code)}</span>
            <span className="text-muted-foreground text-xs">+{country.dial}</span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[300px] p-0"
          align="start"
          data-testid={`${testid}-country-popover`}
        >
          <Command>
            <CommandInput
              placeholder="Search country…"
              data-testid={`${testid}-country-search`}
            />
            <CommandList className="max-h-[280px]">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map((c) => (
                  <CommandItem
                    key={`${c.code}-${c.dial}`}
                    value={`${c.name} ${c.code} +${c.dial}`}
                    onSelect={() => pickCountry(c)}
                    data-testid={`${testid}-country-${c.code}`}
                  >
                    <span className="mr-2 text-base leading-none">{codeToFlag(c.code)}</span>
                    <span className="flex-1 text-sm">{c.name}</span>
                    <span className="text-muted-foreground text-xs">+{c.dial}</span>
                    {country.code === c.code && country.dial === c.dial && (
                      <Check className="w-3.5 h-3.5 ml-2 text-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        {...inputProps}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={local}
        onChange={handleLocalChange}
        placeholder={placeholder}
        className="rounded-l-none flex-1 min-w-0"
        data-testid={`${testid}-number-input`}
      />
    </div>
  );
}
